import axios from 'axios';
import { basicAuthConfig } from '../configs';
import { urlMyspreadApi } from '../urls';

/**
 * Obtener rutas paginadas y filtradas
 * @param {Object} user - Usuario autenticado
 * @param {Object} params - { ruta_id, conductor_id, empresa_id, tms_id, tipo_precio, fecha_desde, fecha_hasta, pageSize, pageNumber, searchValue }
 * @returns {Promise} Response con lista de rutas y total
 */
export const getRutas = async (user, params = {}) => {
  try {
    const config = basicAuthConfig(user);
    let endpoint = urlMyspreadApi.replace(/\/$/, '') + '/mantenedores/rutas/?';
    const query = [];
    if (params.ruta_id) query.push(`ruta_id=${encodeURIComponent(params.ruta_id)}`);
    if (params.conductor_id) query.push(`conductor_id=${encodeURIComponent(params.conductor_id)}`);
    if (params.empresa_id) query.push(`empresa_id=${params.empresa_id}`);
    if (params.tms_id) query.push(`tms_id=${params.tms_id}`);
    if (params.tipo_precio) query.push(`tipo_precio=${encodeURIComponent(params.tipo_precio)}`);
    if (params.fecha_desde) query.push(`fecha_desde=${encodeURIComponent(params.fecha_desde)}`);
    if (params.fecha_hasta) query.push(`fecha_hasta=${encodeURIComponent(params.fecha_hasta)}`);
    if (params.searchValue && params.searchValue.length > 0) query.push(`searchValue=${encodeURIComponent(params.searchValue)}`);
    if (params.pageSize) query.push(`pageSize=${params.pageSize}`);
    if (params.pageNumber) query.push(`pageNumber=${params.pageNumber}`);
    endpoint += query.join('&');
    const response = await axios.get(endpoint, config);
    if (response.data) {
      return {
        success: true,
        data: response.data.data,
        total: response.data.total,
        message: response.data.message
      };
    }
    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: [],
      total: 0
    };
  } catch (error) {
    console.error('Error al obtener rutas:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener rutas',
      data: [],
      total: 0
    };
  }
};

/**
 * Buscar usuarios móviles con rutas por ID de usuario móvil
 * @param {Object} user - Usuario autenticado
 * @param {number} usuarioMovilId - ID del usuario móvil
 * @returns {Promise} Response con lista de rutas del usuario móvil
 */
export const searchUsuariosMoviles = async (user, usuarioMovilId) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/rutas/search/usuario-movil?usuario_movil_id=${usuarioMovilId}`;
    
    const response = await axios.get(endpoint, config);
    
    return {
      success: true,
      data: response.data.data || [],
      total: response.data.total || 0,
      message: response.data.message || 'Búsqueda completada exitosamente'
    };
  } catch (error) {
    console.error('Error al buscar rutas del usuario móvil:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al buscar rutas del usuario móvil',
      data: [],
      total: 0
    };
  }
};

/**
 * Obtener guías de una ruta específica
 * @param {Object} user - Usuario autenticado
 * @param {number} rutaId - ID de la ruta
 * @returns {Promise} Response con lista de guías de la ruta
 */
export const getGuiasRuta = async (user, rutaId) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/rutas/${rutaId}/guias`;
    
    const response = await axios.get(endpoint, config);
    
    return {
      success: true,
      data: response.data.data || [],
      total: response.data.total || 0,
      message: response.data.message || 'Guías obtenidas exitosamente'
    };
  } catch (error) {
    console.error('Error al obtener guías de la ruta:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener guías de la ruta',
      data: [],
      total: 0
    };
  }
};

/**
 * Obtener guías de una ruta específica
 * @param {Object} user - Usuario autenticado
 * @param {number} rutaId - ID de la ruta
 * @returns {Promise} Response con lista de guías de la ruta
 */
export const getRutaById = async (user, rutaId) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/rutas/${rutaId}`;
    
    const response = await axios.get(endpoint, config);
    
    return {
      success: true,
      data: response.data.data || [],
      message: response.data.message || 'Guías obtenidas exitosamente'
    };
  } catch (error) {
    console.error('Error al obtener guías de la ruta:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener guías de la ruta',
      data: [],
      total: 0
    };
  }
};

/**
 * Crear nueva ruta
 * @param {Object} user - Usuario autenticado
 * @param {Object} rutaData - Datos de la ruta a crear
 * @returns {Promise} Response con resultado de la operación
 */
export const createRuta = async (user, rutaData) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/rutas/`;
    
    const response = await axios.post(endpoint, rutaData, config);
    
    return {
      success: true,
      data: response.data.data || {},
      message: response.data.message || 'Ruta creada exitosamente'
    };
  } catch (error) {
    console.error('Error al crear ruta:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al crear ruta'
    };
  }
};

/**
 * Actualizar asignación de precio de una ruta
 * @param {Object} user - Usuario autenticado
 * @param {number} rutaId - ID de la ruta
 * @param {Object} priceData - Datos del precio { tipo_precio: 'fijo' | 'variable', precio_fijo: number }
 * @returns {Promise} Response con resultado de la operación
 */
export const updateRutaPrice = async (user, rutaId, updateData) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/rutas/${rutaId}`;
    
    const body = {};
    if (updateData.hasOwnProperty('tipo_precio')) body.tipo_precio = updateData.tipo_precio;
    if (updateData.hasOwnProperty('precio_fijo')) body.precio_fijo = updateData.precio_fijo;
    if (updateData.hasOwnProperty('usuario_movil_id')) body.usuario_movil_id = updateData.usuario_movil_id;
    
    const response = await axios.put(endpoint, body, config);
    
    return {
      success: true,
      data: response.data.data || {},
      message: response.data.message || 'Precio de ruta actualizado exitosamente'
    };
  } catch (error) {
    console.error('Error al actualizar precio de ruta:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al actualizar precio de ruta'
    };
  }
};

/**
 * Descargar plantilla Excel para carga masiva de rutas
 * @param {Object} user - Usuario autenticado
 * @returns {Promise} Response con resultado de la descarga
 */
export const downloadPlantillaBulk = async (user) => {
  try {
    const config = {
      ...basicAuthConfig(user),
      responseType: 'blob'
    };
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/rutas/plantilla-bulk`;
    
    const response = await axios.get(endpoint, config);
    
    // Extraer nombre del archivo del header Content-Disposition
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'plantilla_rutas_bulk.xlsx';
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    // Crear URL del blob y descargar
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return {
      success: true,
      message: 'Plantilla descargada exitosamente'
    };
  } catch (error) {
    console.error('Error al descargar plantilla:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al descargar plantilla'
    };
  }
};

/**
 * Descargar plantilla Excel para edición masiva de rutas
 * @param {Object} user - Usuario autenticado
 * @returns {Promise} Response con resultado de la descarga
 */
export const downloadPlantillaEdicionBulk = async (user) => {
  try {
    const config = {
      ...basicAuthConfig(user),
      responseType: 'blob'
    };
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/rutas/plantilla-bulk-edicion`;

    const response = await axios.get(endpoint, config);

    // Extraer nombre del archivo del header Content-Disposition
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'plantilla_edicion_rutas.xlsx';

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Crear URL del blob y descargar
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return {
      success: true,
      message: 'Plantilla descargada exitosamente'
    };
  } catch (error) {
    console.error('Error al descargar plantilla de edición:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al descargar plantilla'
    };
  }
};

/**
 * Subir archivo Excel para edición masiva de rutas (procesamiento ASÍNCRONO)
 * Actualiza usuario móvil, tipo de precio y precio fijo cruzando por código de ruta.
 * El backend valida en la misma request y luego inicia un proceso en background;
 * la respuesta trae un process_id para consultar el avance con getRutasBulkStatus.
 * @param {Object} user - Usuario autenticado
 * @param {File} file - Archivo Excel a subir
 * @returns {Promise} Response con process_id y los conteos iniciales de validación
 */
export const updateRutasBulk = async (user, file) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/rutas/bulk/update`;

    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(endpoint, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data'
      }
    });

    // El backend devuelve status 202 (Accepted) para proceso asíncrono
    if (response.status === 202 && response.data) {
      return {
        success: response.data.success ?? true,
        processId: response.data.process_id,
        totalFilas: response.data.total_filas || 0,
        filasProcesadas: response.data.filas_procesadas || 0,
        filasOmitidas: response.data.filas_omitidas || 0,
        totalOperaciones: response.data.total_operaciones || 0,
        advertencias: response.data.advertencias || [],
        erroresValidacion: response.data.errores_validacion || [],
        message: response.data.message,
        statusUrl: response.data.status_url
      };
    }

    return {
      success: true,
      message: response.data?.message || 'Archivo subido exitosamente'
    };
  } catch (error) {
    console.error('Error al subir archivo de edición masiva:', error);

    const errorData = error.response?.data || {};

    return {
      success: false,
      error: errorData.message || errorData.error || 'Error al procesar el archivo',
      errores: errorData.errores || [],
      totalErrores: errorData.total_errores || 0,
      filasOmitidas: errorData.filas_omitidas || 0,
      totalFilas: errorData.total_filas || 0
    };
  }
};

/**
 * Consultar el estado de un proceso masivo de rutas (creación o edición)
 * Usa el mismo endpoint que ya existe para trackear la carga masiva de creación
 * @param {Object} user - Usuario autenticado
 * @param {string} processId - ID del proceso a consultar
 * @returns {Promise} Response con processInfo (status, progress, message, etc.)
 */
export const getRutasBulkStatus = async (user, processId) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/rutas/bulk/status/${processId}`;

    const response = await axios.get(endpoint, config);

    return {
      success: response.data?.success ?? false,
      processInfo: response.data?.process_info || null
    };
  } catch (error) {
    console.error('Error al consultar estado del proceso:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Error al consultar el estado del proceso'
    };
  }
};

/**
 * Subir archivo Excel para carga masiva de rutas
 * @param {Object} user - Usuario autenticado
 * @param {File} file - Archivo Excel a subir
 * @returns {Promise} Response con resultado del proceso
 */
export const uploadRutasBulk = async (user, file) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/rutas/bulk/create`;
    
    // Crear FormData para enviar el archivo
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(endpoint, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data'
      }
    });
    
    // El backend devuelve status 202 (Accepted) para proceso asíncrono
    if (response.status === 202 && response.data) {
      return {
        success: response.data.success || true,
        processId: response.data.process_id,
        totalRutasUnicas: response.data.total_rutas_unicas,
        totalGuias: response.data.total_guias,
        message: response.data.message,
        statusUrl: response.data.status_url
      };
    }
    
    return {
      success: true,
      message: response.data?.message || 'Archivo subido exitosamente'
    };
  } catch (error) {
    console.error('Error al subir archivo bulk:', error);
    
    // Si hay datos de error detallados del backend, incluirlos en la respuesta
    const errorData = error.response?.data || {};
    
    return {
      success: false,
      error: errorData.message || errorData.error || 'Error al procesar el archivo',
      errores: errorData.errores || [],
      total_errores: errorData.total_errores || 0,
      fase: errorData.fase || null
    };
  }
};
